import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { PrepareConnectorFlowStepResponse } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import { usePrepareConnectorFlowQuery, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SOURCE_CONNECTOR_WIZARD_DIRECTION } from '../constants';
import {
  collectSchemaDefaults,
  getEntityFormFromPreparedFlow,
  getEntityFormFromPrepareStepResponse,
  getStaticEntityFormFromFlow,
  isMultiStepConnectorFlow,
} from '../sourceConnectorFlowForms';
import { SourceConnectorWizardData } from '../types';

type SourceConnectorEntityStepProps = {
  wizardData: SourceConnectorWizardData;
  onNext: (data: Partial<SourceConnectorWizardData>) => void;
  onPrev: () => void;
  onCancel: () => void;
};

export const SourceConnectorEntityStep: FC<SourceConnectorEntityStepProps> = ({ wizardData, onNext, onPrev }) => {
  const { t } = useTranslation('data_sources');
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const [formData, setFormData] = useState<Record<string, unknown>>(wizardData.params);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [step1Response, setStep1Response] = useState<PrepareConnectorFlowStepResponse | null>(null);

  const flow = wizardData.flow;
  const flowId = flow?.id ?? '';
  const isMultiStep = useMemo(() => isMultiStepConnectorFlow(flow), [flow]);

  const { data: preparedFlow, isLoading: isLoadingPrepare } = usePrepareConnectorFlowQuery({ workspaceId, id: flowId }, { skip: !flowId || isMultiStep });
  const [prepareStep1, { isLoading: isLoadingStep1 }] = usePrepareConnectorFlowStepMutation();

  useEffect(() => {
    if (!isMultiStep || !flowId) return;

    const loadStep1 = async () => {
      setPrepareError(null);
      try {
        const response = await prepareStep1({
          workspaceId,
          flowId,
          stepId: 'step1',
          stepData: {},
          direction: SOURCE_CONNECTOR_WIZARD_DIRECTION,
        }).unwrap();
        setStep1Response(response);
      } catch (error: unknown) {
        const message = (error as { data?: { message?: string } })?.data?.message ?? t('wizard.source_connector.entity_form_unavailable');
        setPrepareError(message);
      }
    };

    loadStep1();
  }, [flowId, isMultiStep, prepareStep1, t, workspaceId]);

  const entityForm = useMemo(() => {
    const staticFallback = getStaticEntityFormFromFlow(flow);

    if (isMultiStep) {
      if (!step1Response) return staticFallback;
      const translated = translateI18nValues(step1Response, currentLanguage);
      return getEntityFormFromPrepareStepResponse(translated, 'step1') || staticFallback;
    }

    if (!preparedFlow) return staticFallback;

    const translated = translateI18nValues(preparedFlow, currentLanguage);
    return getEntityFormFromPreparedFlow(translated) || staticFallback;
  }, [currentLanguage, flow, isMultiStep, preparedFlow, step1Response]);

  useEffect(() => {
    if (!entityForm?.schema || Object.keys(wizardData.params).length > 0) return;

    setFormData((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return collectSchemaDefaults(entityForm.schema);
    });
  }, [entityForm, wizardData.params]);

  const isEntityValid = useMemo(() => {
    if (!entityForm?.schema) return false;
    const { errors } = validator.validateFormData(formData, entityForm.schema);
    return errors.length === 0;
  }, [entityForm, formData]);
  const canProceed = isEntityValid && Boolean(entityForm);

  const handleNext = () => {
    onNext({ params: formData });
  };

  const isLoading = isMultiStep ? isLoadingStep1 && !step1Response : isLoadingPrepare;

  return (
    <Loadable isLoading={isLoading}>
      <div className='flex max-w-2xl flex-col gap-6'>
        {entityForm ? (
          <Form
            schema={entityForm.schema}
            uiSchema={entityForm.uiSchema || {}}
            formData={formData}
            onChange={({ formData: nextFormData }) => setFormData(nextFormData)}
            validator={validator}
          >
            <Fragment />
          </Form>
        ) : (
          <p className='text-sm text-secondary-600'>{prepareError ?? t('wizard.source_connector.entity_form_unavailable')}</p>
        )}

        <StepPageActions
          primaryButton={{ text: 'COMMON.next', onClick: handleNext, disabled: !canProceed }}
          secondaryButton={{ text: 'COMMON.back', onClick: onPrev }}
        />
      </div>
    </Loadable>
  );
};
